import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { stories } from '@/mocks/stories';

const navItems = [
  { label: '首页', to: '/' },
  { label: '今日热瓜', to: '/#hot' },
  { label: '主题分类', to: '/#categories' },
  { label: '线索追踪', to: '/#track' },
  { label: '吃瓜方法论', to: '/#guide' },
  { label: '常见问题', to: '/#faq' },
  { label: '关于我们', to: '/about' },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    if (!location.hash) return;
    const id = decodeURIComponent(location.hash.replace('#', ''));
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
    return () => window.clearTimeout(timer);
  }, [location.hash, location.pathname]);

  const results = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return [];
    return stories
      .filter(
        (item) =>
          item.title.toLowerCase().includes(keyword) ||
          item.tags.some((tag) => tag.toLowerCase().includes(keyword)) ||
          item.category.toLowerCase().includes(keyword),
      )
      .slice(0, 5);
  }, [query]);

  const isActive = (to: string) => {
    const [path] = to.split('#');
    if (path === '/about') return location.pathname === '/about';
    if (to.includes('#')) return false;
    return location.pathname === '/';
  };

  const closeAll = () => {
    setMenuOpen(false);
    setSearchOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full items-center justify-between gap-4 px-4 md:px-6">
        <Link to="/" onClick={closeAll} className="flex shrink-0 items-center gap-2 cursor-pointer">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-600 text-background-50">
            <i className="ri-leaf-line text-lg" />
          </span>
          <span className="font-heading text-xl font-black tracking-tight text-foreground-950">
            西瓜瓜田
          </span>
        </Link>

        <nav className="hidden items-center gap-1 xl:flex" aria-label="主导航">
          {navItems.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              onClick={closeAll}
              className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                isActive(item.to)
                  ? 'bg-primary-100 font-medium text-primary-800'
                  : 'text-foreground-700 hover:bg-background-100 hover:text-primary-700'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="relative hidden md:block">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
            <input
              type="search"
              value={query}
              onFocus={() => setSearchOpen(true)}
              onChange={(e) => {
                setQuery(e.target.value);
                setSearchOpen(true);
              }}
              onBlur={() => {
                window.setTimeout(() => setSearchOpen(false), 180);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setQuery('');
                  setSearchOpen(false);
                }
              }}
              placeholder="搜索瓜料 / 主角 / 标签"
              aria-label="搜索瓜料"
              className="w-56 rounded-md border border-background-300 bg-background-100 py-2 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
            {searchOpen && query.trim() && (
              <div className="absolute right-0 top-12 w-72 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                {results.length > 0 ? (
                  results.map((item) => (
                    <Link
                      key={item.id}
                      to={`/melon/${item.id}`}
                      onClick={() => {
                        setQuery('');
                        setSearchOpen(false);
                      }}
                      className="block cursor-pointer border-b border-background-200 px-4 py-3 text-sm text-foreground-800 transition-colors last:border-b-0 hover:bg-background-100 hover:text-primary-700"
                    >
                      <span className="line-clamp-1 font-medium">{item.title}</span>
                      <span className="mt-1 block text-xs text-foreground-500">{item.category}</span>
                    </Link>
                  ))
                ) : (
                  <p className="px-4 py-4 text-sm text-foreground-500">没有找到相关瓜料，换个关键词试试</p>
                )}
              </div>
            )}
          </div>

          <Link
            to="/#hot"
            onClick={closeAll}
            className="hidden cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:inline-flex"
          >
            今日热瓜
          </Link>

          <button
            type="button"
            aria-label="打开菜单"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 transition-colors hover:bg-background-100 xl:hidden"
          >
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 xl:hidden">
          <nav className="mx-auto grid max-w-[1400px] grid-cols-2 gap-1 px-4 py-3 md:px-6" aria-label="移动端导航">
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={closeAll}
                className="cursor-pointer rounded-md px-3 py-3 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="px-4 pb-4 md:px-6">
            <button
              type="button"
              onClick={() => {
                closeAll();
                navigate('/#hot');
              }}
              className="w-full cursor-pointer rounded-md bg-primary-500 px-4 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              今天有什么瓜？
            </button>
          </div>
        </div>
      )}
    </header>
  );
}